/**
 * Template Selector Component
 * Dropdown button to select and apply report templates
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    Button, Menu, MenuItem, ListSubheader, Typography, Box,
    Divider, Chip, CircularProgress, Tooltip
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDispatch, useSelector } from 'react-redux';
import { getTemplatesRequest, getTemplateRequest, clearSelectedTemplate } from '../../redux/modules/reportTemplate/slice';

/**
 * Template Selector
 * @param {Function} onApplyTemplate - Callback when template is applied (receives { clinicalNotes, findings, impression })
 * @param {Array} examList - Current exam list to help determine relevant category
 */
const TemplateSelector = ({ onApplyTemplate, examList = [] }) => {
    const dispatch = useDispatch();
    const { loading, templates, groupedTemplates, selectedTemplate } = useSelector(state => state.reportTemplate);
    const { currentBranch } = useSelector(state => state.branch);

    const [anchorEl, setAnchorEl] = useState(null);
    const [pendingTemplateId, setPendingTemplateId] = useState(null);
    const open = Boolean(anchorEl);

    // Fetch templates on mount
    useEffect(() => {
        if (currentBranch) {
            dispatch(getTemplatesRequest({}));
        }
    }, [dispatch, currentBranch]);

    // Apply template when full data is loaded
    useEffect(() => {
        if (selectedTemplate && pendingTemplateId === selectedTemplate._id) {
            onApplyTemplate({
                clinicalNotes: selectedTemplate.clinicalNotes || '',
                findings: selectedTemplate.findings || '',
                impression: selectedTemplate.impression || ''
            });
            setPendingTemplateId(null);
            dispatch(clearSelectedTemplate());
            handleClose();
        }
    }, [selectedTemplate, pendingTemplateId, onApplyTemplate, dispatch]);

    // Detect suggested category based on exam list
    const suggestedCategory = useMemo(() => {
        const examNames = examList?.flatMap(e => e.list?.map(l => l?.name?.toLowerCase()) || []) || [];

        // Check for OB keywords
        const obKeywords = ['ob', 'obstetric', 'fetal', 'pregnancy', 'prenatal', 'anatomy scan', 'biometry', 'growth'];
        if (examNames.some(name => name && obKeywords.some(kw => name.includes(kw)))) {
            return 'OB';
        }

        // Check for GYN keywords
        const gynKeywords = ['gyn', 'pelvic', 'uterus', 'ovary', 'ovarian'];
        if (examNames.some(name => name && gynKeywords.some(kw => name.includes(kw)))) {
            return 'GYN';
        }

        // Check for other categories
        const categoryKeywords = {
            'Abdominal': ['abdomen', 'abdominal', 'liver', 'gallbladder', 'ruq', 'pancreas', 'spleen'],
            'Thyroid': ['thyroid'],
            'Breast': ['breast'],
            'Renal': ['renal', 'kidney', 'bladder'],
            'Vascular': ['carotid', 'duplex', 'vascular', 'venous', 'arterial', 'dvt'],
            'Cardiac': ['cardiac', 'echo', 'heart'],
        };

        for (const [category, keywords] of Object.entries(categoryKeywords)) {
            if (examNames.some(name => name && keywords.some(kw => name.includes(kw)))) {
                return category;
            }
        }

        return null;
    }, [examList]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleTemplateSelect = (templateId) => {
        setPendingTemplateId(templateId);
        dispatch(getTemplateRequest({ id: templateId }));
    };

    // Build menu items grouped by category
    const menuItems = useMemo(() => {
        const items = [];

        // Get categories that have templates
        const categoriesWithTemplates = Object.entries(groupedTemplates)
            .filter(([_, group]) => group.templates?.length > 0)
            .sort((a, b) => {
                // Put suggested category first
                if (a[0] === suggestedCategory) return -1;
                if (b[0] === suggestedCategory) return 1;
                return a[1].label.localeCompare(b[1].label);
            });

        categoriesWithTemplates.forEach(([category, group], index) => {
            if (index > 0) {
                items.push(<Divider key={`divider-${category}`} />);
            }

            items.push(
                <ListSubheader
                    key={`header-${category}`}
                    sx={{
                        bgcolor: 'background.paper',
                        lineHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    {group.label}
                    {category === suggestedCategory && (
                        <Chip
                            size="small"
                            label="Suggested"
                            color="primary"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                    )}
                </ListSubheader>
            );

            group.templates.forEach(template => {
                items.push(
                    <MenuItem
                        key={template._id}
                        onClick={() => handleTemplateSelect(template._id)}
                        sx={{ pl: 3, py: 1 }}
                        disabled={pendingTemplateId === template._id}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {pendingTemplateId === template._id ? (
                                <CircularProgress size={16} />
                            ) : template.isNormal ? (
                                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            ) : (
                                <ArticleOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            )}
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2">{template.name}</Typography>
                                {template.description && (
                                    <Typography variant="caption" color="text.secondary">
                                        {template.description}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </MenuItem>
                );
            });
        });

        return items;
    }, [groupedTemplates, suggestedCategory, pendingTemplateId]);

    const hasTemplates = templates.length > 0;

    return (
        <>
            <Tooltip title={hasTemplates ? "Apply a report template" : "No templates available"}>
                <span>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ArticleOutlinedIcon />}
                        endIcon={<KeyboardArrowDownIcon />}
                        onClick={handleClick}
                        disabled={loading || !hasTemplates}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                    >
                        Templates
                    </Button>
                </span>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        maxHeight: 400,
                        width: 320,
                    }
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : menuItems.length > 0 ? (
                    menuItems
                ) : (
                    <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                            No templates available
                        </Typography>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default TemplateSelector;
